import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { custodyFilesApi } from '../services/api';
import {
    CustodyAccount,
    CustodyHoldingFile,
    CustodyIngestResponse,
    CustodySkippedRow,
} from '../types/client';
import { Trash2, FileText, Loader2, Download, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import {
    PageFrame, PageHeader, Surface, Button, Note, Pill, Placeholder, FileDropzone, uiStyles,
} from '@/components/ui';
import { pageStyles } from '@/components/utils/PageLayout';
import ErrorDisplay from '@/components/utils/ErrorDisplay';
import shared from '@/components/utils/shared.module.css';
import styles from './CustodyHoldingsProcess.module.css';

const ACCEPTED_EXTENSIONS = ['.zip', '.xlsx', '.xls', '.csv'];
const PAGE_NOTE =
    "Upload a custody statement to write that account's holdings for the chosen date. "
    + 'Uploading replaces whatever the account had for that date, and the statement is '
    + 'refused if the date printed on it is not the one selected.';
/** Sentinel for the account select — every account, for the morning "did it all arrive" view. */
const ALL_ACCOUNTS = '';

interface IngestOutcome {
    success: boolean;
    message: string;
    details: string[];
}

/** The one sentence worth showing, from either an ingest refusal or a field error.
 *
 *  The ingest endpoint answers a refusal with the whole result body — counters, the
 *  stored file, the lot — so picking values off it indiscriminately renders as
 *  "false, 0, 0, [object Object]". Field errors arrive in DRF's shape instead.
 */
const errorMessage = (data: any): string => {
    if (!data) return 'Processing failed';
    if (typeof data === 'string') return data;
    if (data.error || data.detail) return data.error || data.detail;

    const fieldErrors = Object.values(data)
        .flat()
        .filter((value): value is string => typeof value === 'string');
    return fieldErrors[0] ?? 'Processing failed';
};

/** Each unresolved row names a master to fix, so they are listed rather than counted. */
const describeSkipped = (skipped: CustodySkippedRow[]): string[] => {
    const clients = [...new Set(skipped.filter(r => r.reason.includes('Client')).map(r => r.client_code))];
    const isins = [...new Set(skipped.filter(r => r.reason.includes('Company')).map(r => r.isin))];
    return [
        clients.length ? `Clients not found: ${clients.join(', ')}` : '',
        isins.length ? `ISINs not mapped to a company: ${isins.join(', ')}` : '',
    ].filter(Boolean);
};

const CustodyHoldingsProcess: React.FC = () => {
    const [accounts, setAccounts] = useState<CustodyAccount[]>([]);
    const [accountId, setAccountId] = useState<number | ''>(ALL_ACCOUNTS);
    const [files, setFiles] = useState<CustodyHoldingFile[]>([]);
    const [filesLoading, setFilesLoading] = useState(true);
    const [statement, setStatement] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [outcome, setOutcome] = useState<IngestOutcome | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);

    const account = useMemo(
        () => accounts.find(a => a.id === accountId),
        [accounts, accountId],
    );
    const isApiBased = account?.is_api_based ?? false;
    const isAllAccounts = accountId === ALL_ACCOUNTS;

    useEffect(() => {
        let cancelled = false;
        custodyFilesApi.getAccounts()
            .then(response => {
                if (cancelled) return;
                setAccounts(Array.isArray(response) ? response : response.results ?? []);
            })
            .catch(() => { if (!cancelled) toast.error('Failed to load custody accounts'); });
        return () => { cancelled = true; };
    }, []);

    const fetchFiles = useCallback(async () => {
        setFilesLoading(true);
        try {
            const response = await custodyFilesApi.getFiles({
                custody_holding_date: selectedDate,
                ...(account ? { custody_code: account.custody_code } : {}),
            });
            setFiles(response.results);
        } catch {
            toast.error('Failed to load files for this date');
        } finally {
            setFilesLoading(false);
        }
    }, [account, selectedDate]);

    useEffect(() => { fetchFiles(); }, [fetchFiles]);

    const accountByCode = useMemo(
        () => new Map(accounts.map(a => [a.custody_code, a])),
        [accounts],
    );

    /* Only accounts that actually have a statement — an account with nothing uploaded
       has no file to download or delete, so it earns no row. */
    const rows = useMemo(
        () => files.map(file => ({ file, account: accountByCode.get(file.custody_code) })),
        [files, accountByCode],
    );

    /* Named in one line below the table instead: still worth seeing which file-based
       accounts have not arrived, without a row per account saying nothing. */
    const awaited = useMemo(
        () => accounts.filter(
            a => !a.is_api_based && !files.some(file => file.custody_code === a.custody_code),
        ),
        [accounts, files],
    );

    const handleAccountChange = (value: string) => {
        setAccountId(value === ALL_ACCOUNTS ? ALL_ACCOUNTS : Number(value));
        setStatement(null);
        setOutcome(null);
    };

    const handleFileChange = (picked: File[]) => {
        const selected = picked[0];
        if (!selected) return;
        if (!ACCEPTED_EXTENSIONS.some(ext => selected.name.toLowerCase().endsWith(ext))) {
            toast.error(`Only ${ACCEPTED_EXTENSIONS.join(', ')} files are allowed.`);
            return;
        }
        setStatement(selected);
        setOutcome(null);
    };

    const handleSubmit = async () => {
        if (!accountId) return;

        setLoading(true);
        setOutcome(null);
        try {
            if (isApiBased) {
                const result = await custodyFilesApi.fetchApiHoldings(accountId, selectedDate);
                setOutcome({
                    success: true,
                    message: `${result.written} holdings fetched from the broker`,
                    details: [],
                });
                toast.success('Holdings fetched');
            } else {
                if (!statement) return;
                const result: CustodyIngestResponse =
                    await custodyFilesApi.uploadFile(statement, accountId, selectedDate);
                setOutcome({
                    success: true,
                    message: `${result.written} holdings written${result.ignored ? `, ${result.ignored} on the ignore list` : ''}`,
                    details: describeSkipped(result.skipped ?? []),
                });
                setStatement(null);
                toast.success('Statement processed');
            }
        } catch (error: any) {
            // A rejected statement is still stored, so the table below is refreshed either way.
            const data = error.response?.data;
            setOutcome({
                success: false,
                message: errorMessage(data),
                // Only the rows it could not resolve — the rest of the body is counters.
                details: describeSkipped(data?.skipped ?? []),
            });
            toast.error(errorMessage(data));
        } finally {
            setLoading(false);
            fetchFiles();
        }
    };

    const handleDelete = async (file: CustodyHoldingFile) => {
        const count = file.holdings_count ?? 0;
        if (!window.confirm(
            `Delete this statement? ${count} holding${count === 1 ? '' : 's'} written from it will be removed too.`,
        )) return;

        try {
            await custodyFilesApi.deleteFile(file.id);
            toast.success('Statement and its holdings removed');
            fetchFiles();
        } catch {
            toast.error('Failed to delete statement');
        }
    };

    const canSubmit = Boolean(accountId) && (isApiBased || Boolean(statement));
    const submitIcon = loading
        ? <Loader2 className={styles.spinner} size={16} />
        : isApiBased
            ? <RefreshCw size={15} />
            : undefined;
    const submitLabel = loading ? 'Processing…' : isApiBased ? 'Fetch Holdings' : 'Process Statement';

    return (
        <PageFrame
            header={
                <PageHeader
                    title="Custody Holdings Process"
                    subtitle="Upload or fetch each account's custody statement for a given date."
                />
            }
        >
            <Note tone="info">{PAGE_NOTE}</Note>

            <Surface title="Process Statement">
                <div className={styles['settings-list']}>
                    <div className={styles['setting-row']}>
                        <div className={styles['setting-info']}>
                            <span className={styles['setting-label']}>Custody Account</span>
                            <span className={styles['setting-hint']}>
                                Pick an account to upload or fetch its statement, or All accounts to review what has arrived for the date below.
                            </span>
                        </div>
                        <select
                            id="custody_account"
                            value={accountId}
                            onChange={e => handleAccountChange(e.target.value)}
                            disabled={loading}
                            className={pageStyles.input}
                            style={{ width: 260, flex: 'none' }}
                        >
                            <option value={ALL_ACCOUNTS}>All accounts</option>
                            {accounts.map(a => (
                                <option key={a.id} value={a.id}>
                                    {a.scheme_name ?? a.custody_code} ({a.custody_code})
                                    {a.is_api_based ? ' — broker API' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles['setting-row']}>
                        <div className={styles['setting-info']}>
                            <span className={styles['setting-label']}>Holding Date</span>
                            <span className={styles['setting-hint']}>
                                The date printed on the statement — uploading replaces this account&apos;s holdings for that date.
                            </span>
                        </div>
                        <input
                            id="custody_date"
                            type="date"
                            value={selectedDate}
                            onChange={e => setSelectedDate(e.target.value)}
                            disabled={loading}
                            className={pageStyles.input}
                            style={{ width: 260, flex: 'none' }}
                        />
                    </div>
                </div>

                {!isAllAccounts && !isApiBased && (
                    <div className={styles['upload-single']}>
                        <FileDropzone
                            title="Custody Statement"
                            accept={ACCEPTED_EXTENSIONS}
                            onFiles={handleFileChange}
                            file={statement}
                            onRemove={() => setStatement(null)}
                            mainText="Click or drag & drop the statement"
                            subText={`Replaces this account's holdings for the selected date · ${ACCEPTED_EXTENSIONS.join(', ').toUpperCase()}`}
                            inputId="custody_statement_upload"
                            busy={loading}
                        />
                    </div>
                )}

                {isAllAccounts ? (
                    <p className={`${styles.hint} ${styles['no-account-hint']}`}>
                        Select an account above to upload or fetch its statement.
                    </p>
                ) : (
                    <div className={styles['form-actions']}>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={loading || !canSubmit}
                            icon={submitIcon}
                        >
                            {submitLabel}
                        </Button>
                    </div>
                )}

                {outcome && (
                    /* The shared banner the rest of the app uses — warning when rows were
                       written but some could not be resolved, since that needs a master fixed. */
                    <ErrorDisplay
                        type={outcome.success ? (outcome.details.length ? 'warning' : 'info') : 'error'}
                        title={outcome.success ? 'Statement processed' : 'Statement not processed'}
                        message={[outcome.message, ...outcome.details].join(' · ')}
                    />
                )}
            </Surface>

            <Surface
                title="Statements"
                meta={`For ${format(new Date(selectedDate), 'MMM dd, yyyy')}`}
            >
                <div className={shared['table-scroll']}>
                    <table className={shared.table}>
                        <thead>
                            <tr>
                                <th>Scheme</th>
                                <th>Custody Code</th>
                                <th>File</th>
                                <th>Uploaded</th>
                                <th className={shared['text-right']}>Holdings</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(({ account: row, file }) => (
                                <tr key={file.id}>
                                    <td>{row?.scheme_name ?? '—'}</td>
                                    <td>{file.custody_code || '—'}</td>
                                    <td>
                                        <div className={styles['file-name-cell']}>
                                            <FileText size={16} color="var(--text-faint)" />
                                            <span
                                                className={styles['file-name-link']}
                                                onClick={() => window.open(file.file, '_blank')}
                                            >
                                                {file.file_name || file.file.split('/').pop()?.split('?')[0]}
                                            </span>
                                        </div>
                                    </td>
                                    <td>{file.created_ts ? format(new Date(file.created_ts), 'MMM dd, hh:mm a') : '—'}</td>
                                    <td className={shared['text-right']}>{file.holdings_count ?? 0}</td>
                                    <td>
                                        {file.status === 'FAILED' ? (
                                            <Pill tone="critical" title={file.error_detail}>Failed</Pill>
                                        ) : (
                                            <Pill tone="positive">Success</Pill>
                                        )}
                                    </td>
                                    <td>
                                        {/* Flex lives on a div, not the td: display:flex on a cell
                                            drops table-cell and the row stops aligning. */}
                                        <div className={styles['action-cell']}>
                                            <a
                                                href={file.file}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={uiStyles.iconBtn}
                                                title="Download statement"
                                            >
                                                <Download size={14} />
                                            </a>
                                            <Button
                                                iconOnly
                                                onClick={() => handleDelete(file)}
                                                title="Delete statement and its holdings"
                                                aria-label="Delete statement and its holdings"
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={7}>
                                        {filesLoading
                                            ? <Placeholder kind="loading" message="Loading…" />
                                            : <Placeholder message="No statement uploaded for this date" />}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Surface>

            {isAllAccounts && awaited.length > 0 && (
                <p className={styles['awaited-note']}>
                    Awaiting a statement for: {awaited.map(a => a.custody_code).join(', ')}
                </p>
            )}
            {rows.some(({ file }) => file.status === 'FAILED') && (
                <p className={styles.hint} style={{ marginTop: '1rem' }}>
                    {rows.find(({ file }) => file.status === 'FAILED')?.file.error_detail}
                </p>
            )}
        </PageFrame>
    );
};

export default CustodyHoldingsProcess;
